// @givmo/board — the director's ONE transport: the organization door, `/board-director/organizations`.
//
// Directors sign in with their own Givmo accounts (CBG-SEC2, D-CBG-16/17), so a director call is the
// signed-in account's, exactly like an officer call: each member below is its generated operation,
// bound by reference, passing no `client`, so it rides the host's configured singleton — the account's
// Bearer, App Check and the host's request prefix. There is no second client, no session header and no
// director-level step-up: the board session and its isolated client retired with D-CBG-16.
//
// THE ORGANIZATION DOOR SERVES EVERY CLASS. A board is named by its `org_ref` whatever it is; the
// backend reads the class off the seat. So, unlike the officer port, no host re-spells this one: both
// consoles pass `createOrgBoardDirectorOperations()`, and the older door outside `/organizations` is
// never called again.
//
// CAPACITY, NOT CREDENTIAL. The account is the same in both capacities, so what keeps a director's act
// from being recorded as an officer's is that director surfaces reach only THIS port and officer
// surfaces only `board-operations.ts` (`director/capacity-rail.test.ts`).
//
// Every member is generated since the one regeneration from landed `development` (SEC2-BE1b): the
// claim's three routes graduated from their `givmoClient` hatches with no call site changing.

import { sdk } from '@givmo/api-client';

/** The director operations over the organization door, one generated operation per member. */
export function createOrgBoardDirectorOperations() {
  return {
    // ── The account's own: its boards and window, its upcoming meetings, and the claim ──
    directorGetBoardDirectorMe: sdk.boardDirectorOrgGetBoardDirectorMe,
    directorListBoardDirectorUpcomingMeetings:
      sdk.boardDirectorOrgListBoardDirectorUpcomingMeetings,
    directorPreviewBoardInvitation: sdk.boardDirectorOrgPreviewBoardInvitation,
    directorRequestBoardSeatClaimCode: sdk.boardDirectorOrgRequestBoardSeatClaimCode,
    directorClaimBoardSeat: sdk.boardDirectorOrgClaimBoardSeat,
    // CBG-SEC2-L4-MOVE (D-CBG-23 flow (b)): a director's move of their own seat to another account —
    // the preview, the code and the finish, by the move's ticket (org-door only).
    directorPreviewBoardSeatMove: sdk.boardDirectorOrgPreviewBoardSeatMove,
    directorRequestBoardSeatMoveCode: sdk.boardDirectorOrgRequestBoardSeatMoveCode,
    directorCompleteBoardSeatMove: sdk.boardDirectorOrgCompleteBoardSeatMove,

    // ── One board's, named by `org_ref` ──
    directorGetBoardDirectorAttention: sdk.boardDirectorOrgGetBoardDirectorAttention,
    // CBG-UX7-WEB-DIR: what this seat did on this board in the last 90 days, the to-do list's
    // Completed (a read: no signing window).
    directorGetBoardDirectorAttentionCompleted:
      sdk.boardDirectorOrgGetBoardDirectorAttentionCompleted,
    // CBG-SEC2-L4-MOVE: the move's start, signed in to the account the seat is on.
    directorStartBoardSeatMove: sdk.boardDirectorOrgStartBoardSeatMove,
    directorListBoardDirectorRecords: sdk.boardDirectorOrgListBoardDirectorRecords,
    directorGetBoardDirectorRecord: sdk.boardDirectorOrgGetBoardDirectorRecord,
    directorAcknowledgeBoardRecord: sdk.boardDirectorOrgAcknowledgeBoardRecord,
    // CBG-RC10: a director who was in a closed session signing to approve its minutes.
    directorSignBoardMinutesToApprove: sdk.boardDirectorOrgSignBoardMinutesToApprove,
    directorListBoardDirectorConsents: sdk.boardDirectorOrgListBoardDirectorConsents,
    directorGetBoardDirectorConsent: sdk.boardDirectorOrgGetBoardDirectorConsent,
    directorSignBoardWrittenConsent: sdk.boardDirectorOrgSignBoardWrittenConsent,
    directorRevokeBoardWrittenConsentSignature:
      sdk.boardDirectorOrgRevokeBoardWrittenConsentSignature,
    // CBG-YR4-PAY-CONSENT: one of a consent's compensation documents, opened before signing (a read:
    // no signing window).
    directorGetBoardCompensationDocumentDownloadForDirector:
      sdk.boardDirectorOrgGetBoardCompensationDocumentDownloadForDirector,
    directorListOwnBoardConflictDisclosures: sdk.boardDirectorOrgListOwnBoardConflictDisclosures,
    directorRecordOwnBoardConflictDisclosure: sdk.boardDirectorOrgRecordOwnBoardConflictDisclosure,
    // CBG-YR2-WEB2: the year's conflict-of-interest questionnaire — this seat's cycles, its
    // questionnaire, the signed answers, and its own copy and changes.
    directorListOwnBoardCoiCycles: sdk.boardDirectorOrgListOwnBoardCoiCycles,
    directorGetOwnBoardCoiQuestionnaire: sdk.boardDirectorOrgGetOwnBoardCoiQuestionnaire,
    directorSubmitOwnBoardCoiResponse: sdk.boardDirectorOrgSubmitOwnBoardCoiResponse,
    directorGetOwnBoardCoiResponse: sdk.boardDirectorOrgGetOwnBoardCoiResponse,
    directorGetOwnBoardCoiResponseChanges: sdk.boardDirectorOrgGetOwnBoardCoiResponseChanges,
    directorGetOwnBoardCoiResponseDocument: sdk.boardDirectorOrgGetOwnBoardCoiResponseDocument,
    directorListBoardDirectorMeetings: sdk.boardDirectorOrgListBoardDirectorMeetings,
    directorGetBoardDirectorMeeting: sdk.boardDirectorOrgGetBoardDirectorMeeting,
    directorGetBoardDirectorMeetingCalendar: sdk.boardDirectorOrgGetBoardDirectorMeetingCalendar,
    directorRecordBoardDirectorAttendanceIntent:
      sdk.boardDirectorOrgRecordBoardDirectorAttendanceIntent,
    directorGetBoardDirectorMeetingRecord: sdk.boardDirectorOrgGetBoardDirectorMeetingRecord,
    directorSignBoardMinutes: sdk.boardDirectorOrgSignBoardMinutes,
    directorGetBoardDirectorPacket: sdk.boardDirectorOrgGetBoardDirectorPacket,
    directorGetBoardDirectorPacketDocumentDownloadUrl:
      sdk.boardDirectorOrgGetBoardDirectorPacketDocumentDownloadUrl,
    directorGetBoardDirectorFilingReceiptDownloadUrl:
      sdk.boardDirectorOrgGetBoardDirectorFilingReceiptDownloadUrl,
    directorListBoardDirectorAnnotations: sdk.boardDirectorOrgListBoardDirectorAnnotations,
    directorCreateBoardDirectorAnnotation: sdk.boardDirectorOrgCreateBoardDirectorAnnotation,
    directorDeleteBoardDirectorAnnotation: sdk.boardDirectorOrgDeleteBoardDirectorAnnotation,
    // CBG-YR4-START, -ABOUT: the member's own getting-started list, their mark on one of its steps
    // (an answer, like an RSVP: no signing window), and About the board.
    directorGetOwnBoardGettingStarted: sdk.boardDirectorOrgGetOwnBoardGettingStarted,
    directorMarkOwnBoardGettingStartedStep: sdk.boardDirectorOrgMarkOwnBoardGettingStartedStep,
    directorGetBoardAbout: sdk.boardDirectorOrgGetBoardAbout,
    // CBG-YR4-ASSESS-WEB: the board's self-assessment — the ones this seat was asked in, its
    // questions with the person's own answers, sending them (an ordinary act on the sign-in: an
    // opinion, not a statement of fact, so no signing window), and the results once closed.
    directorListOwnBoardSelfAssessments: sdk.boardDirectorOrgListOwnBoardSelfAssessments,
    directorGetOwnBoardSelfAssessmentQuestions:
      sdk.boardDirectorOrgGetOwnBoardSelfAssessmentQuestions,
    directorSaveOwnBoardSelfAssessmentAnswers:
      sdk.boardDirectorOrgSaveOwnBoardSelfAssessmentAnswers,
    directorGetBoardSelfAssessmentResultsForDirector:
      sdk.boardDirectorOrgGetBoardSelfAssessmentResultsForDirector,
    // CBG-YR5-DIST: a copy of the return provided to the board — the page's read, and the file's
    // link, called only when the person opens it: a recipient's first call IS the receipt.
    directorGetBoardReturnCopyForDirector: sdk.boardDirectorOrgGetBoardReturnCopyForDirector,
    directorOpenBoardReturnCopyForDirector: sdk.boardDirectorOrgOpenBoardReturnCopyForDirector,
  };
}

/** Every host's director port is this object's type: the organization door's generated signatures. */
export type BoardDirectorOperations = ReturnType<typeof createOrgBoardDirectorOperations>;
